import { Link } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Bell, CircleDollarSign, ShoppingBag, Scale, TrendingUp, ShoppingCart, HandCoins, BarChart3, Receipt, ArrowDownLeft, ArrowUpRight, ChefHat } from "lucide-react";
import { useApi } from "@/pos/lib/api";
import { useAuth } from "@/pos/context/AuthContext";
import { rm } from "@/pos/lib/format";
import { Empty } from "@/pos/components/Common";

const greet = () => {
  const h = new Date().getHours();
  return h < 12 ? "Selamat pagi" : h < 15 ? "Selamat tengah hari" : h < 19 ? "Selamat petang" : "Selamat malam";
};

function MiniStat({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="flex flex-col items-center text-center" data-testid={testid}>
      <div className={`mb-1.5 grid h-9 w-9 place-items-center rounded-xl text-white ${color}`}><Icon size={18} /></div>
      <div className="text-[10px] text-slate-500">{label}</div>
      <div className="num text-sm font-bold text-slate-900">{value}</div>
    </div>
  );
}

function QuickActions({ isOwner }) {
  const items = [
    { to: "/pos/order", label: "Jualan Baru", icon: ShoppingCart, cls: "bg-red-100 text-red-700 border-red-300" },
    { to: "/pos/dapur", label: "Dapur", icon: ChefHat, cls: "bg-red-100 text-red-700 border-red-200" },
    { to: "/pos/pelanggan", label: "Rekod Hutang", icon: HandCoins, cls: "bg-rose-100 text-rose-600 border-rose-200" },
    isOwner ? { to: "/pos/laporan", label: "Laporan", icon: BarChart3, cls: "bg-indigo-100 text-indigo-600 border-indigo-200" } : { to: "/pos/jualan", label: "Senarai Bil", icon: BarChart3, cls: "bg-indigo-100 text-indigo-600 border-indigo-200" },
  ];
  return (
    <div className="grid grid-cols-4 gap-2.5">
      {items.map((a) => (
        <Link key={a.label} to={a.to} className={`quick-tile text-center leading-tight ${a.cls}`} data-testid={`quick-${a.label.replace(/\s/g, "-").toLowerCase()}`}>
          <a.icon size={22} />{a.label}
        </Link>
      ))}
    </div>
  );
}

function SalesChart({ data }) {
  return (
    <div className="card-surface p-3" data-testid="home-sales-chart">
      <div className="strip-title mb-3"><span>Gambaran Jualan</span><span className="rounded bg-white/20 px-2 py-0.5 text-xs">Hari Ini</span></div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ left: -18, right: 8, top: 8 }}>
            <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#EF4444" stopOpacity={0.45} /><stop offset="100%" stopColor="#EF4444" stopOpacity={0.02} /></linearGradient></defs>
            <CartesianGrid vertical={false} stroke="#FCE4E1" />
            <XAxis dataKey="hour" fontSize={11} tickLine={false} axisLine={false} interval="preserveStartEnd" />
            <YAxis fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip formatter={(v) => rm(v)} />
            <Area type="monotone" dataKey="jualan" name="Jualan" stroke="#DC2626" strokeWidth={2.5} fill="url(#g)" dot={{ r: 2.5, fill: "#DC2626" }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function TopProducts({ items }) {
  return (
    <div data-testid="home-top-products">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Produk Terlaris Hari Ini</h3>
        <Link to="/pos/jualan" className="rounded-md bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">Lihat Semua</Link>
      </div>
      <div className="space-y-2">
        {items.length === 0 ? <div className="card-surface"><Empty text="Belum ada jualan hari ini" /></div> : items.map((p) => (
          <div key={p.id} className="card-surface flex items-center gap-3 p-2.5">
            {p.image ? <img src={p.image} alt="" className="h-11 w-11 rounded-lg object-cover" /> : <div className="h-11 w-11 rounded-lg bg-red-50" />}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{p.name}</div>
              <div className="text-xs text-slate-500">{rm(p.total)}</div>
            </div>
            <div className="text-right">
              <div className="num text-lg font-bold leading-none">{p.qty}</div>
              <div className="text-[10px] text-slate-500">{p.unit} terjual</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OwnerCash({ d }) {
  const rows = [["Duit Masuk hari ini", d.today_cash_in, ArrowDownLeft, "text-green-700", "stat-cash-in"], ["Duit Keluar hari ini", d.today_cash_out, ArrowUpRight, "text-rose-600", "stat-cash-out"]];
  return (
    <div className="grid grid-cols-2 gap-2.5">
      {rows.map(([l, v, I, cls, tid]) => (
        <div key={l} className="card-surface p-3" data-testid={tid}>
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500"><I size={14} />{l}</div>
          <div className={`num mt-1 text-lg font-bold ${cls}`}>{rm(v)}</div>
        </div>
      ))}
    </div>
  );
}

function DebtCard({ d }) {
  return (
    <Link to="/pos/pelanggan" className="card-surface flex items-center justify-between p-4" data-testid="stat-total-debt">
      <div>
        <div className="text-xs text-slate-500">Hutang Belum Dibayar · {d.debtors} pelanggan</div>
        <div className="num text-2xl font-bold text-amber-600">{rm(d.total_debt)}</div>
      </div>
      <div className="grid h-11 w-11 place-items-center rounded-xl bg-amber-100 text-amber-600"><HandCoins size={22} /></div>
    </Link>
  );
}

function CashierHome({ d, user, shop }) {
  return (
    <div className="mx-auto max-w-[900px] space-y-5 p-4 sm:p-6 lg:p-8" data-testid="cashier-home">
      <header className="animate-rise">
        <div className="text-xs text-slate-600">{greet()}, {user.name}</div>
        <div className="font-heading text-lg font-bold" data-testid="home-shop-name">{shop?.shop_name || "Kedai"}</div>
      </header>
      <Link to="/pos/dapur" className="hero-green flex items-center justify-between rounded-2xl p-5 text-white shadow-lg shadow-red-600/20" data-testid="cashier-open-orders">
        <div>
          <div className="text-sm text-red-50/90">Order sedang berjalan</div>
          <div className="num text-4xl font-bold">{d.open_orders}</div>
        </div>
        <ChefHat size={36} />
      </Link>
      <div>
        <h3 className="mb-2 text-sm font-semibold">Tindakan Pantas</h3>
        <QuickActions isOwner={false} />
      </div>
      <DebtCard d={d} />
    </div>
  );
}

export default function Home() {
  const { data: d } = useApi("/dashboard");
  const { data: shop } = useApi("/settings");
  const { user, isOwner } = useAuth();
  if (!d) return <div className="p-6 text-slate-500">Memuatkan...</div>;
  if (!isOwner) return <CashierHome d={d} user={user} shop={shop} />;
  const avg = d.today_count ? d.today_sales / d.today_count : 0;
  return (
    <div className="mx-auto max-w-[1400px] p-4 sm:p-6 lg:p-8">
      <header className="mb-4 flex items-center justify-between animate-rise">
        <div>
          <div className="text-xs text-slate-600">{greet()}, {user.name}</div>
          <div className="font-heading text-lg font-bold" data-testid="home-shop-name">{shop?.shop_name || "Kedai"}</div>
          <div className="text-[11px] text-slate-500">Hari ini, {new Date().toLocaleDateString("ms-MY", { day: "numeric", month: "short", year: "numeric" })}</div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/pos/dapur" className="relative grid h-10 w-10 place-items-center rounded-full bg-red-100 text-red-700" data-testid="home-bell">
            <Bell size={18} />{d.open_orders > 0 && <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-rose-500" />}
          </Link>
          <div className="grid h-10 w-10 place-items-center rounded-full bg-[#2A0707] font-bold text-white">{user.name[0]}</div>
        </div>
      </header>
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-5">
          <div className="hero-green overflow-hidden rounded-2xl text-white shadow-lg shadow-red-600/20 animate-rise" data-testid="stat-today-sales">
            <div className="px-4 pb-3 pt-4">
              <div className="text-sm text-red-50/90">Jualan Hari Ini</div>
              <div className="num mt-0.5 text-4xl font-bold tracking-tight">{rm(d.today_sales)}</div>
            </div>
            <div className="grid grid-cols-4 gap-1 rounded-t-2xl bg-white px-2 py-3">
              <MiniStat icon={CircleDollarSign} color="bg-blue-500" label="Jumlah" value={rm(d.today_sales)} testid="mini-total" />
              <MiniStat icon={ShoppingBag} color="bg-red-500" label="Transaksi" value={d.today_count} testid="mini-count" />
              <MiniStat icon={Scale} color="bg-rose-500" label="Item Dijual" value={d.today_items} testid="stat-today-kg" />
              <MiniStat icon={TrendingUp} color="bg-indigo-500" label="Purata Bil" value={rm(avg)} testid="mini-avg" />
            </div>
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold">Tindakan Pantas</h3>
            <QuickActions isOwner={isOwner} />
          </div>
          <SalesChart data={d.hourly} />
        </div>
        <div className="space-y-5">
          <DebtCard d={d} />
          {isOwner && <OwnerCash d={d} />}
          <TopProducts items={d.top_today} />
        </div>
      </div>
    </div>
  );
}
